import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { pathsAPI } from '../api/pathsAPI';
import useCapabilityNames from '../hooks/useCapabilityNames';
import { launchButton } from '../utils/launchButton';
import {
  groupItemsByPhase,
  pathProgressLabel,
  phaseCountLabel,
  phaseStates,
  showsPhaseHeadings,
} from '../utils/phaseStatus';
import { displayText } from '../utils/displayText';
import { MY_PATH_URL } from '../utils/pathsChatUrl';
import PhaseSection from './PhaseSection';
import PlanItemRow from './PlanItemRow';
import ReviewCheckpointCard from './ReviewCheckpointCard';
import './PlanItemRow.css';
import './PathView.css';

// The one path component, in two layouts:
//   'full'   the main area of /chat with no conversation (plan only)
//   'panel'  beside the path's conversation (split)
//
// It loads the path itself (getPath), reloads after every progress or status
// change, and reloads when reloadKey changes (a revision was accepted).
// Completed and archived paths are read-only: nothing ticks or launches, and
// an archived path offers Restore. Progress is counts of actions, never a bar
// or a percentage (spec §3.7).
//
// The next-action banner takes only the item id from GET /next-action, which
// carries no launch data, and reads that item's title, instruction and
// launch_url from the plan already loaded here. If the id is not in the plan
// the banner falls back to the endpoint's own title with no button.

const READ_ONLY_STATUSES = new Set(['completed', 'archived']);

function missingPrefill(item, button) {
  const title = displayText(item.title);
  const nouns = { topic: 'topic', problem: 'problem', interview_type: 'interview type' };
  const noun = nouns[item.launch_target?.kind] || 'target';
  return `The ${noun} for "${title}" isn't in ${button.appName} any more. Can you suggest a replacement?`;
}

function keyDatePrefill(item) {
  return `The official date for "${displayText(item.title)}" has changed. Can you revise my plan around the new date?`;
}

function Unavailable({ layout, onClose, onRetry }) {
  return (
    <div className={`path-view path-view-${layout} path-view-unavailable`} role="alert">
      <div className="path-view-unavailable-body">
        <h2 className="path-view-title">This path isn&apos;t available</h2>
        <p className="path-view-goal">
          It may have been removed, or it can&apos;t be loaded right now.
        </p>
        <div className="path-view-unavailable-actions">
          <Link className="path-view-btn path-view-btn-primary" to={MY_PATH_URL}>Back to My Path</Link>
          {onRetry && <button type="button" className="path-view-btn" onClick={onRetry}>Try again</button>}
          {layout === 'panel' && onClose && (
            <button type="button" className="path-view-btn" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PathView({
  pathId,
  layout = 'full',
  reloadKey = 0,
  onClose,
  onDiscuss,
  onRevise,
  onPathChanged,
}) {
  const capabilityNames = useCapabilityNames();
  const [path, setPath] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | unavailable
  const [nextAction, setNextAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [statusBusy, setStatusBusy] = useState(false);
  const [reviewItemId, setReviewItemId] = useState(null);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [retryKey, setRetryKey] = useState(0);
  const requestRef = useRef(0);

  const refreshNextAction = useCallback(async (id) => {
    try {
      const response = await pathsAPI.getNextAction(id);
      return response && typeof response === 'object' ? response.next_action || null : null;
    } catch {
      return null; // the banner is a convenience; the plan still shows
    }
  }, []);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const request = ++requestRef.current;
    if (!quiet) setLoadState('loading');
    try {
      const data = await pathsAPI.getPath(pathId);
      if (request !== requestRef.current) return;
      if (!data || typeof data !== 'object' || !data.id) throw new Error('malformed path');
      setPath(data);
      setLoadState('ready');
      const next = READ_ONLY_STATUSES.has(data.status) ? null : await refreshNextAction(pathId);
      if (request === requestRef.current) setNextAction(next);
    } catch {
      if (request === requestRef.current) {
        setPath(null);
        setLoadState('unavailable');
      }
    }
  }, [pathId, refreshNextAction]);

  useEffect(() => {
    setExpanded({});
    setReviewItemId(null);
    setActionError(null);
    load();
  }, [load, reloadKey, retryKey]);

  const items = useMemo(() => (Array.isArray(path?.items) ? path.items : []), [path]);
  const phases = path?.current_version?.phases;
  const groups = useMemo(() => groupItemsByPhase(phases, items), [phases, items]);
  const states = useMemo(() => phaseStates(groups), [groups]);
  const withHeadings = showsPhaseHeadings(groups);
  const readOnly = READ_ONLY_STATUSES.has(path?.status);
  const interactive = !readOnly;
  const currentPhaseIndex = states.indexOf('current');

  const setItemStatus = async (itemId, status) => {
    if (!path || readOnly) return;
    const before = path;
    setActionError(null);
    setPendingIds((prev) => new Set(prev).add(itemId));
    setPath((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.id === itemId ? { ...i, progress_status: status } : i)),
    }));
    try {
      await pathsAPI.updateItem(itemId, status);
      setNextAction(await refreshNextAction(pathId));
    } catch {
      setPath(before); // never leave a failed write looking successful
      setActionError('That change was not saved. Please try again.');
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const changePathStatus = async (status) => {
    if (!path || statusBusy) return;
    if (status === 'completed'
      && !window.confirm('Mark this path complete? It becomes read-only.')) return;
    setStatusBusy(true);
    setActionError(null);
    try {
      const updated = await pathsAPI.updatePathStatus(path.id, status);
      // PATCH returns the bare path row, with no items or version: merge
      // only its own fields so the loaded plan is kept.
      const { items: _items, current_version: _version, ...fields } = updated && typeof updated === 'object'
        ? updated
        : {};
      const merged = { ...path, ...fields, status: fields.status || status };
      setPath(merged);
      if (READ_ONLY_STATUSES.has(merged.status)) setNextAction(null);
      else setNextAction(await refreshNextAction(pathId));
      onPathChanged?.(merged);
    } catch {
      setActionError(status === 'active'
        ? 'The path could not be restored. Please try again.'
        : 'That change was not saved. Please try again.');
    } finally {
      setStatusBusy(false);
    }
  };

  const reviewItem = reviewItemId ? items.find((i) => i.id === reviewItemId) : null;

  const submitReview = async (decision, reflection, reportedOutcome) => {
    if (!reviewItem) return;
    setSubmittingReview(true);
    setActionError(null);
    try {
      await pathsAPI.submitReview(reviewItem.id, { decision, reflection, reportedOutcome });
      setReviewItemId(null);
      if (decision === 'revise') onRevise?.();
      await load({ quiet: true });
    } catch {
      setActionError('The review was not saved. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loadState === 'unavailable') {
    return <Unavailable layout={layout} onClose={onClose} onRetry={() => setRetryKey((k) => k + 1)} />;
  }
  if (loadState === 'loading' || !path) {
    return (
      <div className={`path-view path-view-${layout}`} aria-busy="true">
        <p className="path-view-loading">Loading your path…</p>
      </div>
    );
  }

  const title = displayText(path.title) || 'Untitled path';
  const goal = displayText(path.goal);
  const progress = pathProgressLabel(items);
  const statusNote = path.status === 'completed' ? 'Completed' : path.status === 'archived' ? 'Archived' : null;

  const nextItem = nextAction ? items.find((i) => i.id === nextAction.id) || null : null;
  const nextSource = nextItem || nextAction;
  const nextButton = nextItem && nextItem.item_type !== 'review' ? launchButton(nextItem, capabilityNames) : null;
  const nextIsReview = nextSource?.item_type === 'review';

  const rowProps = (item, phaseIndex) => ({
    item,
    interactive,
    capabilityNames,
    pending: pendingIds.has(item.id),
    canStartReview: interactive && (phaseIndex === currentPhaseIndex || item.id === nextAction?.id),
    onComplete: () => setItemStatus(item.id, 'completed'),
    onSkip: () => setItemStatus(item.id, 'skipped'),
    onUndo: () => setItemStatus(item.id, 'pending'),
    onAskReplacement: onRevise ? (row, button) => onRevise(missingPrefill(row, button)) : undefined,
    onRevisePlan: onRevise ? (row) => onRevise(keyDatePrefill(row)) : undefined,
    onStartReview: (row) => setReviewItemId(row.id),
  });

  const renderItems = (list, phaseIndex) => (
    <div className="path-view-items">
      {list.map((item) => <PlanItemRow key={item.id} {...rowProps(item, phaseIndex)} />)}
    </div>
  );

  return (
    <div className={`path-view path-view-${layout}${readOnly ? ' path-view-read-only' : ''}`}>
      <header className="path-view-head">
        <div className="path-view-head-text">
          <div className="path-view-eyebrow">Path{statusNote ? ` · ${statusNote} · read only` : ''}</div>
          <h2 className="path-view-title">{title}</h2>
          {goal && <p className="path-view-goal">{goal}</p>}
        </div>
        <div className="path-view-head-actions">
          {layout === 'full' && interactive && onDiscuss && (
            <button type="button" className="path-view-btn path-view-btn-primary" onClick={() => onDiscuss()}>
              Discuss in chat
            </button>
          )}
          {interactive && onRevise && (
            <button type="button" className="path-view-btn" onClick={() => onRevise()}>Revise</button>
          )}
          {layout === 'panel' && onClose && (
            <button type="button" className="path-view-btn path-view-icon-btn" onClick={onClose} aria-label="Close plan">
              ✕
            </button>
          )}
        </div>
      </header>

      <div className="path-view-body">
        {actionError && <p className="path-view-error" role="alert">{actionError}</p>}

        {interactive && nextSource && !reviewItem && (
          <div className="path-view-next" aria-label="Next action">
            <div className="path-view-eyebrow path-view-eyebrow-on-ink">
              {nextIsReview ? "It's time for a check-in" : 'Next action'}
            </div>
            <div className="path-view-next-title">{displayText(nextSource.title) || 'Your next step'}</div>
            {displayText(nextSource.instruction) && (
              <div className="path-view-next-text">{displayText(nextSource.instruction)}</div>
            )}
            {nextButton && nextButton.kind === 'app' && (
              <Link className="path-view-next-launch" to={nextButton.href}>
                {nextButton.label} <span aria-hidden="true">→</span>
              </Link>
            )}
            {nextButton && nextButton.kind === 'external' && (
              <a className="path-view-next-launch" href={nextButton.href} target="_blank" rel="noopener noreferrer">
                {nextButton.label}
              </a>
            )}
            {nextIsReview && nextItem && (
              <button type="button" className="path-view-next-launch" onClick={() => setReviewItemId(nextItem.id)}>
                Start review
              </button>
            )}
          </div>
        )}

        {reviewItem && (
          <ReviewCheckpointCard item={reviewItem} onSubmit={submitReview} submitting={submittingReview} />
        )}

        {progress && <p className="path-view-progress">{progress}</p>}

        {items.length === 0 && (
          <p className="path-view-empty">This plan has no items yet.</p>
        )}

        {items.length > 0 && !withHeadings && groups.map((group) => (
          <React.Fragment key={group.phase?.id || 'unphased'}>
            {renderItems(group.items, groups.indexOf(group))}
          </React.Fragment>
        ))}

        {items.length > 0 && withHeadings && groups.map((group, index) => {
          const key = group.phase?.id || 'unphased';
          const isOpen = expanded[key] ?? states[index] === 'current';
          return (
            <PhaseSection
              key={key}
              phase={group.phase || { name: 'Other items' }}
              number={index + 1}
              state={states[index]}
              countLabel={phaseCountLabel(group.items)}
              expanded={isOpen}
              onToggle={() => setExpanded((prev) => ({ ...prev, [key]: !isOpen }))}
            >
              {renderItems(group.items, index)}
            </PhaseSection>
          );
        })}
      </div>

      <footer className="path-view-foot">
        {path.status === 'archived' && (
          <button
            type="button"
            className="path-view-btn path-view-btn-primary"
            disabled={statusBusy}
            onClick={() => changePathStatus('active')}
          >
            {statusBusy ? 'Restoring…' : 'Restore'}
          </button>
        )}
        {interactive && (
          <>
            <button type="button" className="path-view-btn" disabled={statusBusy} onClick={() => changePathStatus('archived')}>
              Archive
            </button>
            <button
              type="button"
              className="path-view-btn path-view-btn-primary"
              disabled={statusBusy}
              onClick={() => changePathStatus('completed')}
            >
              Mark complete
            </button>
          </>
        )}
      </footer>
    </div>
  );
}
